import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Sparkles, Zap, Instagram, Video, Linkedin, Play, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useState } from "react";

// Demo video URL - Kapsalon Yasmin example
const DEMO_VIDEO_URL = "https://mkbvideo.com/api/cloud/videos/aba0d794-73d0-4085-b472-f4aadaf423ba_video.mp4";

const LandingPage = () => {
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const stagger = {
    animate: { transition: { staggerChildren: 0.15 } }
  };

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered",
      description: "Sora 2 genereert professionele video's van je productfoto's"
    },
    {
      icon: <Play className="w-6 h-6" />,
      title: "Nederlandse Voice-over",
      description: "Natuurlijke stemmen met ElevenLabs AI technologie"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Binnen Minuten Klaar",
      description: "Van foto naar advertentie in enkele klikken"
    }
  ];

  const platforms = [
    { icon: <Instagram className="w-8 h-8" />, name: "Instagram", format: "Reels & Stories" },
    { icon: <Video className="w-8 h-8" />, name: "TikTok", format: "Verticale Video" },
    { icon: <Linkedin className="w-8 h-8" />, name: "LinkedIn", format: "Professionele Ads" }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] light-mode">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="xl" linkTo="/" variant="dark" />
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-login-btn" className="text-slate-700 hover:text-slate-900">
                Inloggen
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="nav-register-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                Gratis Starten
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <motion.div 
          className="max-w-7xl mx-auto"
          initial="initial"
          animate="animate"
          variants={stagger}
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p 
                variants={fadeUp}
                className="text-sm font-medium text-indigo-600 uppercase tracking-wider mb-4"
              >
                AI Video Creator voor MKB
              </motion.p>
              <motion.h1 
                variants={fadeUp}
                className="text-5xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-tight mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Maak Advertenties die <span className="text-indigo-600">Verkopen</span>
              </motion.h1>
              <motion.p 
                variants={fadeUp}
                className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg"
              >
                Upload je productfoto, schrijf een korte tekst en laat AI de rest doen. 
                Professionele video-advertenties voor Instagram, TikTok en LinkedIn in minuten.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    data-testid="hero-cta-btn"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-indigo-600/25 transition-all duration-300 active:scale-95"
                  >
                    Start Gratis
                    <Sparkles className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  data-testid="hero-demo-btn"
                  className="border-2 border-slate-300 text-slate-700 px-8 py-6 text-lg rounded-xl hover:bg-slate-50"
                  onClick={() => setShowDemoVideo(true)}
                >
                  Bekijk Demo
                  <Play className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </div>
            
            {/* Hero Image */}
            <motion.div 
              variants={fadeUp}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1761818645943-a3689c34ca03?w=800&q=80" 
                  alt="Modern creative studio"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Video wordt gegenereerd...</p>
                        <p className="text-sm text-slate-500">Instagram Reel • 12 sec</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hoe het werkt
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              In drie simpele stappen van productfoto naar professionele video-advertentie
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-slate-100"
              >
                <div className="w-14 h-14 rounded-xl bg-indigo-600 text-white flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Voor Alle Platforms
            </h2>
            <p className="text-lg text-slate-600">
              Automatisch het juiste formaat voor elk social media platform
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-500 border border-slate-200"
              >
                <div className="w-16 h-16 rounded-2xl bg-slate-900 text-white flex items-center justify-center mx-auto mb-6">
                  {platform.icon}
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {platform.name}
                </h3>
                <p className="text-slate-500">{platform.format}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-indigo-600">
        <motion.div 
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Klaar om te Beginnen?
          </h2>
          <p className="text-xl text-indigo-100 mb-10">
            Maak vandaag nog je eerste AI-gegenereerde video-advertentie
          </p>
          <Link to="/register">
            <Button 
              data-testid="cta-register-btn"
              className="bg-white text-indigo-600 hover:bg-slate-100 px-10 py-6 text-lg rounded-xl font-semibold shadow-lg transition-all duration-300 active:scale-95"
            >
              Gratis Account Aanmaken
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" linkTo="/" variant="light" />
          <p className="text-sm">© 2024 MKBVideo. Alle rechten voorbehouden.</p>
        </div>
      </footer>

      {/* Demo Video Modal */}
      {showDemoVideo && (
        <div 
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowDemoVideo(false)}
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative w-full max-w-4xl bg-slate-900 rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button 
              onClick={() => setShowDemoVideo(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            {/* Video header */}
            <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
              <h3 className="text-xl font-semibold text-white">Voorbeeld: Kapsalon Yasmin</h3>
              <p className="text-slate-400 text-sm">AI-gegenereerde video advertentie voor Instagram</p>
            </div>
            
            {/* Video player */}
            <div className="aspect-video bg-black">
              <video 
                src={DEMO_VIDEO_URL}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              >
                <source src={DEMO_VIDEO_URL} type="video/mp4" />
                Je browser ondersteunt geen video playback.
              </video>
            </div>
            
            {/* CTA */}
            <div className="px-6 py-4 bg-slate-800/50 flex items-center justify-between">
              <p className="text-slate-300 text-sm">
                Wil je ook zulke video's maken voor jouw bedrijf?
              </p>
              <Link to="/register" onClick={() => setShowDemoVideo(false)}>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Start Gratis
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
